import { PilingType } from './piling';
import { ProjectCompany } from './company';
import { User } from './user';

export enum ProjectStatus {
  Pending = 'pending',
  Active = 'active',
  Inactive = 'inactive',
  Archived = 'archived',
}

export interface Location {
  latitude?: number;
  longitude?: number;
  address?: string;
}

/** Display field name → inferred type. Set after CSV import (initializeProjectPilings). */
export type OperatorDisplayFieldTypes = Record<string, 'number' | 'string' | 'boolean'>;

/** Piled module lifecycle (`GET`/`PUT` `/projects/{projectID}/piled`). Separate from `invoicedStatus`. */
export type PiledModuleLifecycleStatus = 'pending' | 'active' | 'completed';

/**
 * Piled module configuration (API: `/projects/{projectID}/piled`).
 * Formerly mixed into the monolithic Project object.
 */
export interface PiledModule {
  projectID: string;
  externalProjectID?: string;
  numPilings: number;
  invoicedStatus?: ModuleInvoicedStatus;
  invoicedDate?: string | null;
  pilingType: PilingType;
  pilingAttributes: PilingAttribute[];
  reworkAttributes: PilingAttribute[];
  qcQuickHits?: string[];
  reworkQuickHits?: string[];
  reworkCompleteQuickHits?: string[];
  operatorDisplayFields?: string[];
  operatorDisplayFieldTypes?: OperatorDisplayFieldTypes;
  companies?: ProjectCompany[];
  newActivity?: boolean;
  pilingRows?: boolean;
  hasPilingRows?: boolean;
  coordinateSystemType?: 'UTM' | 'StatePlane';
  coordinateSystemZone?: number | string;
  coordinateSystemHemisphere?: 'N' | 'S';
  coordinateSystemUnits?: 'm' | 'us-ft' | 'ft';
  mapEnabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
  /**
   * Module lifecycle (separate from project `status` and from `invoicedStatus`).
   * Omitted or legacy rows behave as `active` in clients. Use **`completed`** when the module is finished.
   */
  status?: PiledModuleLifecycleStatus;
}

/** Lifecycle on `PullTestingModule` (same vocabulary as {@link PiledModuleLifecycleStatus}). */
export type PullTestingModuleLifecycleStatus = 'pending' | 'active' | 'completed';

export type PullTestingInstallationFieldDataType =
  | 'number'
  /** Depth-style capture as feet + inches in the UI; stored as decimal feet on the piling (`pushedToDepthFt` / `actualEmbedmentDepthFt`). */
  | 'feetInches'
  /** Drive time: seconds per foot driven (0.01 s resolution) — captured value is `driveTimeSecByFoot` on the piling (number[]). */
  | 'numberArray'
  | 'string'
  | 'boolean'
  | 'singleSelect'
  | 'multiSelect';
export type PullTestingInstallationFieldKey =
  | 'pushToDepth'
  | 'driveTime'
  | 'actualEmbedmentDepth'
  | 'northSouth'
  | 'eastWest'
  | 'comments';

/**
 * One row in Pull Testing module `installation` JSON array.
 * Presence in the array means the field is captured at install time (no separate `enabled` flag).
 */
export interface PullTestingInstallationField {
  key: PullTestingInstallationFieldKey;
  label: string;
  metric: string;
  dataType: PullTestingInstallationFieldDataType;
  options?: string[];
  /**
   * Optional module-level defaults for drive-time capture (seconds per foot, one entry per foot).
   * Usually omitted; operators enter values on each piling.
   */
  driveTimeSecByFoot?: number[] | null;
}

/** Wizard / API default order for installation toggles and `POST` payload rows. */
export const PULL_TEST_INSTALLATION_FIELD_ORDER: readonly PullTestingInstallationFieldKey[] = [
  'pushToDepth',
  'driveTime',
  'actualEmbedmentDepth',
  'northSouth',
  'eastWest',
  'comments',
] as const;

const PULL_TEST_INSTALLATION_FIELD_BLUEPRINTS: Record<
  PullTestingInstallationFieldKey,
  PullTestingInstallationField
> = {
  pushToDepth: {
    key: 'pushToDepth',
    label: 'Push to depth (feet/inches)',
    metric: '',
    dataType: 'feetInches',
  },
  driveTime: {
    key: 'driveTime',
    label: 'Drive time (seconds/foot, hundredths)',
    metric: '',
    dataType: 'numberArray',
  },
  actualEmbedmentDepth: {
    key: 'actualEmbedmentDepth',
    label: 'Actual embedment depth (feet/inches)',
    metric: '',
    dataType: 'feetInches',
  },
  northSouth: {
    key: 'northSouth',
    label: 'North/South angle (degrees)',
    /** Unit hint for install UIs (mobile maps “degrees” / “deg” to ° next to the keypad). */
    metric: 'degrees',
    dataType: 'number',
  },
  eastWest: {
    key: 'eastWest',
    label: 'East/West angle (degrees)',
    metric: 'degrees',
    dataType: 'number',
  },
  comments: {
    key: 'comments',
    label: 'Comments',
    metric: '',
    dataType: 'string',
  },
};

/** True when `key` is a known Pull Testing installation schema field (API `installation` array rows). */
export function isPullTestingInstallationFieldKey(key: string): key is PullTestingInstallationFieldKey {
  return Object.prototype.hasOwnProperty.call(PULL_TEST_INSTALLATION_FIELD_BLUEPRINTS, key);
}

export function pullTestInstallationFieldBlueprint(key: PullTestingInstallationFieldKey): PullTestingInstallationField {
  const b = PULL_TEST_INSTALLATION_FIELD_BLUEPRINTS[key];
  return { ...b };
}

/**
 * Splits a blueprint label into a primary title and optional trailing parenthetical
 * (e.g. `Push to depth` + `(feet/inches)`) for typography that de-emphasizes the unit hint.
 */
export function pullTestingInstallationFieldLabelParts(
  key: PullTestingInstallationFieldKey
): { primary: string; suffix: string | null } {
  const full = pullTestInstallationFieldBlueprint(key).label.trim();
  const m = full.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (m) {
    return { primary: m[1].trim(), suffix: `(${m[2].trim()})` };
  }
  return { primary: full, suffix: null };
}

/** @internal Strip legacy `enabled` / `description` and dedupe by `key` (first wins). */
export function normalizePullTestingInstallationFields(
  rows: readonly PullTestingInstallationField[]
): PullTestingInstallationField[] {
  const seen = new Set<PullTestingInstallationFieldKey>();
  const out: PullTestingInstallationField[] = [];
  for (const raw of rows) {
    const r = raw as PullTestingInstallationField & { enabled?: boolean; description?: string };
    if (r.enabled === false) continue;
    const key = r.key as PullTestingInstallationFieldKey;
    if (!PULL_TEST_INSTALLATION_FIELD_BLUEPRINTS[key]) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    const base = pullTestInstallationFieldBlueprint(key);
    if (typeof r.label === 'string' && r.label.trim()) base.label = r.label.trim();
    /** Ignore empty `metric` from API so blueprint defaults (e.g. `degrees` on angle steps) apply */
    if (typeof r.metric === 'string' && r.metric.trim()) base.metric = r.metric.trim();
    if (key !== 'driveTime' && r.dataType && key !== 'actualEmbedmentDepth' && key !== 'pushToDepth') {
      base.dataType = r.dataType;
    }
    if (r.options?.length) base.options = [...r.options];
    if (Array.isArray(r.driveTimeSecByFoot) && r.driveTimeSecByFoot.length > 0) {
      const secs = r.driveTimeSecByFoot.map(v => Number(v)).filter(Number.isFinite);
      if (secs.length > 0) base.driveTimeSecByFoot = secs;
    }
    out.push(base);
  }
  return out;
}

/**
 * Toggle whether `key` is present in the wizard / module `installation` array.
 * When turning a field on, inserts it at the index implied by
 * {@link PULL_TEST_INSTALLATION_FIELD_ORDER} among the keys still present, so
 * toggling off then on does not move the field to the end of the array.
 */
export function togglePullTestingInstallationFieldKey(
  installation: PullTestingInstallationField[],
  key: PullTestingInstallationFieldKey
): void {
  const existing = installation.findIndex(f => f.key === key);
  if (existing >= 0) {
    installation.splice(existing, 1);
    return;
  }
  const canonicalIndex = PULL_TEST_INSTALLATION_FIELD_ORDER.indexOf(key);
  const row = pullTestInstallationFieldBlueprint(key);
  if (canonicalIndex < 0) {
    installation.push(row);
    return;
  }
  let insertAt = installation.length;
  for (let j = 0; j < installation.length; j++) {
    const other = PULL_TEST_INSTALLATION_FIELD_ORDER.indexOf(installation[j].key);
    if (other >= canonicalIndex) {
      insertAt = j;
      break;
    }
  }
  installation.splice(insertAt, 0, row);
}

/** Install-time field schema persisted on the Pull Testing module (`installation` JSON array). */
export type PullTestingInstallationCapture = PullTestingInstallationField[];

/**
 * Legacy capture booleans (no array / no wrapped `fields`).
 * Use {@link resolvePullTestingInstallationFields} when reading from the API.
 */
export interface PullTestingInstallationCaptureLegacyFlags {
  captureDriveTime?: boolean;
  captureNorthSouth?: boolean;
  captureEastWest?: boolean;
}

/** Non-array shapes still returned by some GET responses until backfilled (`fields` wrapper or capture flags only). */
export type PullTestingInstallationLegacyObject = Partial<PullTestingInstallationCaptureLegacyFlags> & {
  fields?: PullTestingInstallationField[];
};

/** Value of `installation` on module GET — new array or legacy object forms. */
export type PullTestingInstallationFromApi = PullTestingInstallationCapture | PullTestingInstallationLegacyObject;

function synthesizeInstallationFieldsFromLegacyFlags(
  inst?: Partial<PullTestingInstallationCaptureLegacyFlags>
): PullTestingInstallationField[] {
  const out: PullTestingInstallationField[] = [];
  if (inst?.captureDriveTime) out.push(pullTestInstallationFieldBlueprint('driveTime'));
  if (inst?.captureNorthSouth) out.push(pullTestInstallationFieldBlueprint('northSouth'));
  if (inst?.captureEastWest) out.push(pullTestInstallationFieldBlueprint('eastWest'));
  out.push(pullTestInstallationFieldBlueprint('comments'));
  return out;
}

/**
 * Resolves install field definitions for the operator UI.
 * Prefers a top-level **`installation` array**; may fall back to **`fields`** or capture flags when needed.
 */
export function resolvePullTestingInstallationFields(
  installation?: PullTestingInstallationFromApi | null
): PullTestingInstallationField[] {
  if (installation == null) {
    return synthesizeInstallationFieldsFromLegacyFlags(undefined);
  }
  if (Array.isArray(installation)) {
    return installation.length === 0 ? [] : normalizePullTestingInstallationFields(installation);
  }
  const wrapped = installation.fields;
  if (Array.isArray(wrapped) && wrapped.length > 0) {
    return normalizePullTestingInstallationFields(wrapped);
  }
  if (Array.isArray(wrapped) && wrapped.length === 0) {
    return [];
  }
  return synthesizeInstallationFieldsFromLegacyFlags(installation);
}

/**
 * Pull Testing module row (API: `GET/POST/PUT /projects/{projectID}/pull-testing`).
 * Slim project-level config; per-pile protocols live on `PullTestPiling`.
 */
export interface PullTestingModule {
  projectID?: string;
  externalProjectID?: string;
  status?: PullTestingModuleLifecycleStatus;
  invoicedStatus?: ModuleInvoicedStatus;
  invoicedDate?: string | null;
  /** Server-maintained count; incremented/decremented by pull-test piling create/delete transactions. */
  numPilings?: number;
  pilePrefix?: string;
  locationSerialStart?: number;
  numLocations?: number;
  pilingsPerLocation?: number;
  installation?: PullTestingInstallationFromApi;
  configurationRevision?: number;
  moduleInstanceId?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

/** Body for `POST /projects/{projectID}/pull-testing` — creates module only (`status` becomes `pending` on server). */
export interface CreatePullTestingModuleRequest {
  pilePrefix: string;
  locationSerialStart: number;
  numLocations: number;
  pilingsPerLocation: number;
  installation: PullTestingInstallationCapture;
  configurationRevision?: number;
  moduleInstanceId?: string;
}

/** Body for `PUT /projects/{projectID}/pull-testing` (partial allowed). */
export type UpdatePullTestingModuleRequest = Partial<{
  status: PullTestingModuleLifecycleStatus;
  invoicedStatus: ModuleInvoicedStatus;
  invoicedDate: string | null;
  pilePrefix: string;
  locationSerialStart: number;
  numLocations: number;
  pilingsPerLocation: number;
  installation: PullTestingInstallationFromApi;
  configurationRevision: number;
  moduleInstanceId: string;
}>;

export type ModuleType = 'piled' | 'pullTesting';
export type ModuleInvoicedStatus = 'pending' | 'invoiced' | 'NR';

export interface ModuleCompanySummary {
  companyID: string;
  name: string;
}

export interface ConfiguredModuleRow {
  moduleType: ModuleType;
  projectID: string;
  projectName: string;
  projectStatus: ProjectStatus | string;
  externalProjectID?: string;
  companies: ModuleCompanySummary[];
  module: PiledModule | PullTestingModule;
}

export type PullTestPilingStatus =
  | 'awaiting installation'
  | 'soaking'
  | 'awaiting testing'
  | 'completed';

/** Count + percentage bucket from `GET .../pull-testing/status`. */
export interface PullTestingStatusBucket {
  count: number;
  percentage: number;
}

/** Response `data` from `GET /projects/{projectID}/pull-testing/status`. */
export interface PullTestingModuleStatus {
  totalPilings: number;
  awaitingInstallation: PullTestingStatusBucket;
  soaking: PullTestingStatusBucket;
  awaitingTesting: PullTestingStatusBucket;
  completed: PullTestingStatusBucket;
  /** API notes mention refusal counts; include when backend returns it. */
  totalRefused?: number;
}

export function emptyPullTestingModuleStatus(): PullTestingModuleStatus {
  const z: PullTestingStatusBucket = { count: 0, percentage: 0 };
  return {
    totalPilings: 0,
    awaitingInstallation: { ...z },
    soaking: { ...z },
    awaitingTesting: { ...z },
    completed: { ...z },
  };
}

export type PullTestResultsRunStatus = 'not_started' | 'in_progress' | 'completed' | 'skipped' | string;

/** One load-hold row in a `*TestPlan`. */
export interface PullTestPlanStep {
  order: number;
  targetLoadLbs: number;
  holdTimeSec?: number;
}

export interface PullTestPlan {
  steps: PullTestPlanStep[];
  /**
   * Failure threshold: maximum total deflection (inches) allowed before the run fails.
   * Set from the Pull Testing setup wizard test model (not shown per schedule row).
   */
  deflectionFailureCriteriaIn?: number | null;
  /**
   * Lateral only: optional second deflection gauge height (ft) for the entire plan.
   * When present (non-null), the lateral test interface captures a third deflection
   * reading at this height for every load step. When null/absent, only deflection 1
   * and deflection 2 are captured. The value is constant for the whole plan and
   * intentionally NOT duplicated per step.
   */
  lateralSecondDeflectionHeightFt?: number | null;
}

/** Captured values for one plan step (aligned by `order` with the plan). */
export interface PullTestResultsStep {
  order: number;
  appliedLoadLbs?: number | null;
  deflection1In?: number | null;
  deflection2In?: number | null;
  deflection3In?: number | null;
  comments?: string;
}

export interface TensileTestResults {
  status: PullTestResultsRunStatus;
  steps: PullTestResultsStep[];
  /** Set when operator submits completed results (ISO 8601). */
  testTimestamp?: string;
  /** User id who submitted completed results (e.g. Cognito `sub`). */
  testByID?: string;
  /**
   * Read-time display name for {@link testByID} on GET list/one only.
   * Not stored; not accepted on create/PATCH.
   */
  testByName?: string;
}

export interface LateralTestResults {
  status: PullTestResultsRunStatus;
  loadHeightAboveGradeFt?: number | null;
  gaugeHeightIn?: number | null;
  steps: PullTestResultsStep[];
  /** Set when operator submits completed results (ISO 8601). */
  testTimestamp?: string;
  /** User id who submitted completed results (e.g. Cognito `sub`). */
  testByID?: string;
  /**
   * Read-time display name for {@link testByID} on GET list/one only.
   * Not stored; not accepted on create/PATCH.
   */
  testByName?: string;
}

export interface CompressionTestResults {
  status: PullTestResultsRunStatus;
  steps: PullTestResultsStep[];
  /** Set when operator submits completed results (ISO 8601). */
  testTimestamp?: string;
  /** User id who submitted completed results (e.g. Cognito `sub`). */
  testByID?: string;
  /**
   * Read-time display name for {@link testByID} on GET list/one only.
   * Not stored; not accepted on create/PATCH.
   */
  testByName?: string;
}

/**
 * Captured installation metrics stored under {@link PullTestPilingInstallation.fields}.
 * Keys align with persisted API names (`actualEmbedmentDepthFt`, `pushedToDepthFt`, etc.).
 * Depth-style captures are **decimal feet** (feet + inch/12).
 * Extra keys may be added as pull-testing captures custom / module-driven installation fields.
 */
export interface PullTestPilingInstallationFields {
  /** Decimal feet — feet + inches/12 from install capture “Push to depth”. */
  pushedToDepthFt?: number;
  actualDepthFt?: number;
  /** Decimal feet — actual embedment depth (installation field `actualEmbedmentDepth`). */
  actualEmbedmentDepthFt?: number;
  northSouthAngleDeg?: number;
  eastWestAngleDeg?: number;
  /** Ordered drive-time segments (seconds); foot `i` is index `i` (foot 1 → index 0). */
  driveTimeSecByFoot?: number[];
  comments?: string;
  refusal?: boolean;
  capturedAt?: string;
  coordinates?: { lat: number; lng: number; accuracy?: number };
}

/**
 * Nested installation on a pull-test piling (operator **`PATCH`**).
 * Capture values live in **`fields`**; metadata (**`installedTimestamp`**, **`installedByID`**, optional install GPS as
 * WGS84 `latitude` / `longitude`) sits at top level beside **`fields`** (not inside `fields`).
 */
export interface PullTestPilingInstallation {
  fields?: PullTestPilingInstallationFields;
  installedTimestamp?: string;
  installedByID?: string;
  /**
   * Read-time display name for {@link installedByID} on GET list/one only.
   * Not stored; not accepted on create/PATCH.
   */
  installedByName?: string;
  /** Install-site latitude (WGS84) when the operator opts in to GPS capture. */
  latitude?: number;
  /** Install-site longitude (WGS84) when the operator opts in to GPS capture. */
  longitude?: number;
}

/** Safe read of **`installation.fields`** (empty object when missing or invalid). */
export function pullTestPilingInstallationFields(
  installation: PullTestPilingInstallation | null | undefined
): PullTestPilingInstallationFields {
  const raw = installation?.fields;
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as PullTestPilingInstallationFields;
  }
  return {};
}

/** Pull-test piling record (API: `/projects/{projectID}/pull-testing/pilings/...`). */
export interface PullTestPiling {
  pullTestPilingID: string;
  projectID: string;
  status: PullTestPilingStatus;
  locationSerial?: number;
  slotIndex?: number;
  pileIdentifier?: string;
  locationKey?: string;
  embedmentDepthFt?: number;
  /** From wizard step “Load cases” / bulk create. */
  pileLengthFt?: number | null;
  soakTimeHours?: number;
  pileType?: string;
  pileCoating?: string;
  pileSection?: string;
  preExcavDepthFt?: number | null;
  preDrillDiameterIn?: number | null;
  preDrillDepthFt?: number | null;
  piledPilingID?: string;
  /** Legacy / optional display id when different from `pullTestPilingID`. */
  pilingID?: string;
  installation?: PullTestPilingInstallation;
  tensileTestPlan?: PullTestPlan | null;
  tensileTestResults?: TensileTestResults | null;
  lateralTestPlan?: PullTestPlan | null;
  lateralTestResults?: LateralTestResults | null;
  compressionTestPlan?: PullTestPlan | null;
  compressionTestResults?: CompressionTestResults | null;
  installedAt?: string;
  soakUntil?: string;
  /** When `status` is `completed`, pass/fail if the API stores it. */
  testPassed?: boolean;
  notes?: string;
  [key: string]: unknown;
}

/** Request envelope for `POST .../pull-testing/pilings/bulk`. */
export interface BulkCreatePullTestPilingsRequest {
  pilings: Record<string, unknown>[];
}

export type PullTestingReportType = 'pile_test' | 'pile_summary';
export type PullTestingReportScope = 'all' | 'specific';

/**
 * Body for `POST /projects/{projectID}/pull-testing/reports/load-test-log` (Pile Load Test Log `.xlsx`).
 * Async: API returns immediately; recipients receive an email with a download link when the workbook is ready.
 */
export interface PileLoadTestLogReportRequest {
  emails: string[];
  /** When set, only this pull-test piling is included; omit for all completed pilings (sorted by `sequenceNumber`). */
  pullTestPilingID?: string;
}

/**
 * Body for `POST /projects/{projectID}/pull-testing/reports/pile-summary` (Pile Summary `.xlsx`).
 * Async: includes all completed pilings on the project (no per-piling scope selector).
 */
export interface PileSummaryReportRequest {
  emails: string[];
}

/** Sentinel state on standalone Pull Testing client audits (`POST …/pull-testing/project-actions`). */
export interface PullTestingStandaloneAuditState {
  kind?: 'pullTesting_standalone_audit';
  pullTestPilingID?: string;
  recordedAtClient?: string;
}

/** `actionDetails` on Pull Testing rows in the shared **`project-actions`** table. */
export interface PullTestingProjectActionDetails {
  module?: 'pullTesting' | string;
  event?: string;
  recordedAtClient?: string;
  clientUserId?: string;
  details?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Shared **`project-actions`** row (`GET …/pull-testing/events-log`, reports, etc.).
 * `pilingID` is the pull-test piling document id for Pull Testing audits.
 */
export interface ProjectAction {
  projectID: string;
  actionTimestamp: string;
  pilingID: string;
  actionType: string;
  /** Top-level module discriminator on shared `project-actions` rows (`"pullTesting"`, `"piled"`, …). */
  module?: string;
  userId: string;
  previousState?: PullTestingStandaloneAuditState | Record<string, unknown>;
  newState?: PullTestingStandaloneAuditState | Record<string, unknown>;
  actionDetails?: PullTestingProjectActionDetails | Record<string, unknown>;
}

/** Semantic buckets on events-log rows and for `eventCategory` query filters. */
export type PullTestEventCategory =
  | 'install_performed'
  | 'soak_bypass'
  | 'tensile_test_performed'
  | 'lateral_test_performed'
  | 'compression_test_performed'
  | 'modality_test_completed'
  | 'tensile_test_completed'
  | 'lateral_test_completed'
  | 'compression_test_completed'
  | 'piling_installed'
  | 'offline_sync_collision'
  | 'offline_sync_collision_resolved'
  | 'validation_alert';

/**
 * One row from `GET /projects/{projectID}/pull-testing/events-log`.
 * Full {@link ProjectAction} envelope plus server-resolved display name.
 */
export interface PullTestErrorLogEntry extends ProjectAction {
  /** Resolved from `userId` (Cognito); falls back to `userId` when lookup fails. */
  userName?: string;
  /** Map of user ids referenced on the row to resolved display names. */
  userDisplayNames?: Record<string, string>;
  /** Semantic buckets for log tabs/badges (server-resolved; inferred for legacy rows). */
  eventCategories?: PullTestEventCategory[];
  /** UUID for offline sync collision rows (`actionDetails.collisionId` mirror). */
  collisionId?: string;
}

/** Alias for {@link PullTestErrorLogEntry} (API docs name). */
export type PullTestEventsLogEntry = PullTestErrorLogEntry;

/** Query params for `GET /projects/{projectID}/pull-testing/events-log`. */
export interface PullTestingEventsLogQuery {
  startDate?: string;
  endDate?: string;
  pullTestPilingID?: string;
  /**
   * Filter rows whose resolved actor display name matches (case-insensitive substring).
   * Replaces `userId` for the event log UI.
   */
  userName?: string;
  /** @deprecated Prefer {@link PullTestingEventsLogQuery.userName}. */
  userId?: string;
  /** 1–1000; omit for server default scan window. */
  limit?: number;
  /** Pagination token from a prior response `nextKey`. */
  startKey?: string;
  /** Optional filter; comma-separated list of `actionType` values (events-log API). */
  actionType?: string;
  /** Optional filter; comma-separated semantic buckets (preferred for log tabs). */
  eventCategory?: string;
  /** Filter collision rows by `actionDetails.resolutionStatus` (requires collision scope). */
  collisionStatus?: 'pending_review' | 'resolved';
}

/** @deprecated Use {@link PullTestingEventsLogQuery}. */
export type PullTestingErrorLogQuery = PullTestingEventsLogQuery;

/**
 * Client-originated Pull Testing audit payload for `POST .../pull-testing/project-actions`.
 * Request body requires **`moduleType`: `"pullTesting"`**; the server persists it as top-level **`module`**.
 */
export const PULL_TESTING_PROJECT_ACTION_MODULE_TYPE = 'pullTesting' as const;

export type PullTestingProjectActionModuleType = typeof PULL_TESTING_PROJECT_ACTION_MODULE_TYPE;
export type PullTestingClientActionType =
  | 'pull_test_deflection_gauge_misalignment'
  | 'pull_test_applied_load_exceeds_next_target'
  | 'pull_test_max_deflection_limit_exceeded'
  | 'pull_test_empty_load_step_skipped'
  | 'pull_test_monotonic_deflection_override'
  | 'pull_test_validation_alert_dismissed'
  | 'pull_test_soak_bypass_alert_confirmed'
  | 'pull_test_zero_target_nonzero_applied_load'
  | 'pull_test_installation_past_embedment_depth'
  | string;

/** Where a validation alert was shown (for {@link PullTestValidationAlertDismissedActionPayload}). */
export type PullTestValidationAlertDismissedSource =
  | 'load_step_advance'
  | 'quick_nav'
  | 'final_submit';

/** Who recorded the operator action (`POST …/pull-testing/project-actions`). */
export interface PullTestProjectActionContext {
  userId: string;
  /** ISO 8601 when the client detected the violation (operator device clock). */
  recordedAt: string;
}

/** @deprecated Use {@link PullTestProjectActionContext}. */
export type PullTestDeflectionGaugeMisalignmentContext = PullTestProjectActionContext;

/** Shared hold-step fields on pull-test operator validation project-actions. */
export interface PullTestHoldStepActionDetailsBase {
  testType: 'tensile' | 'lateral' | 'compression';
  /** Plan step `order` for this hold row. */
  holdStepOrder: number;
  targetLoadLbs: number;
  /** Applied load keyed in by operator for this hold (lbs), if captured. */
  appliedLoadEnteredLbs?: number | null;
  deflection1In?: number | null;
  deflection2In?: number | null;
  deflection3In?: number | null;
  /** Operator-facing pile tag (e.g. `PT-12`). */
  displayId?: string;
}

/**
 * Hold-level readings for **`actionType`** **`pull_test_deflection_gauge_misalignment`**.
 */
export interface PullTestDeflectionGaugeMisalignmentDetails extends PullTestHoldStepActionDetailsBase {
  deflection1In: number;
  deflection2In: number;
  /**
   * Gauge disagreement: `|d1 - d2| / max(|d1|, |d2|, ε)` (e.g. `0.211` = 21.1% off).
   */
  relativeGaugeDifference: number;
}

/**
 * POST body for gauge misalignment audit (nested shape agreed with backend).
 * `projectID` comes from the URL path — omit from body.
 */
export interface PullTestDeflectionGaugeMisalignmentActionPayload {
  moduleType: PullTestingProjectActionModuleType;
  actionType: 'pull_test_deflection_gauge_misalignment';
  pullTestPilingID: string;
  context: PullTestProjectActionContext;
  details: PullTestDeflectionGaugeMisalignmentDetails;
}

export interface PullTestAppliedLoadExceedsNextTargetDetails extends PullTestHoldStepActionDetailsBase {
  appliedLoadEnteredLbs: number;
  nextTargetLoadLbs: number;
}

export interface PullTestAppliedLoadExceedsNextTargetActionPayload {
  moduleType: PullTestingProjectActionModuleType;
  actionType: 'pull_test_applied_load_exceeds_next_target';
  pullTestPilingID: string;
  context: PullTestProjectActionContext;
  details: PullTestAppliedLoadExceedsNextTargetDetails;
}

export interface PullTestMaxDeflectionExceededDetails extends PullTestHoldStepActionDetailsBase {
  maxDeflectionLimitIn: number;
  maxDeflectionEnteredIn: number;
  operatorDecision: 'skip_to_residual' | 'proceed';
}

export interface PullTestMaxDeflectionExceededActionPayload {
  moduleType: PullTestingProjectActionModuleType;
  actionType: 'pull_test_max_deflection_limit_exceeded';
  pullTestPilingID: string;
  context: PullTestProjectActionContext;
  details: PullTestMaxDeflectionExceededDetails;
}

export interface PullTestEmptyLoadStepSkippedDetails extends PullTestHoldStepActionDetailsBase {}

export interface PullTestEmptyLoadStepSkippedActionPayload {
  moduleType: PullTestingProjectActionModuleType;
  actionType: 'pull_test_empty_load_step_skipped';
  pullTestPilingID: string;
  context: PullTestProjectActionContext;
  details: PullTestEmptyLoadStepSkippedDetails;
}

export interface PullTestMonotonicDeflectionOverrideDetails extends PullTestHoldStepActionDetailsBase {
  referenceHoldStepOrder: number;
  referenceTargetLoadLbs: number;
  violatedGauge: 'deflection1' | 'deflection2' | 'deflection3';
  referenceDeflectionIn: number;
  enteredDeflectionIn: number;
  operatorDecision: 'move_on';
}

export interface PullTestMonotonicDeflectionOverrideActionPayload {
  moduleType: PullTestingProjectActionModuleType;
  actionType: 'pull_test_monotonic_deflection_override';
  pullTestPilingID: string;
  context: PullTestProjectActionContext;
  details: PullTestMonotonicDeflectionOverrideDetails;
}

/**
 * Operator dismissed a validation alert without correcting data (incomplete fields, monotonic warning
 * shown only via quick nav / final submit, etc.). Distinct from typed audits that include full hold-step payloads.
 */
export interface PullTestValidationAlertDismissedDetails {
  validationKind: string;
  failureMessage: string;
  source: PullTestValidationAlertDismissedSource;
  holdStepIndex0Based?: number;
  testType?: 'tensile' | 'lateral' | 'compression';
  displayId?: string;
  /** Optional structured context (e.g. monotonic reference row). */
  extra?: Record<string, unknown>;
}

export interface PullTestValidationAlertDismissedActionPayload {
  moduleType: PullTestingProjectActionModuleType;
  actionType: 'pull_test_validation_alert_dismissed';
  pullTestPilingID: string;
  context: PullTestProjectActionContext;
  details: PullTestValidationAlertDismissedDetails;
}

/** Operator confirmed the soak-period bypass alert and the soak bypass API succeeded. */
export interface PullTestSoakBypassAlertConfirmedDetails {
  displayId: string;
  /** Piling status before bypass (expected `soaking`). */
  priorStatus: string;
  /** Reason sent to `POST …/soak/bypass`. */
  reason: string;
}

export interface PullTestSoakBypassAlertConfirmedActionPayload {
  moduleType: PullTestingProjectActionModuleType;
  actionType: 'pull_test_soak_bypass_alert_confirmed';
  pullTestPilingID: string;
  context: PullTestProjectActionContext;
  details: PullTestSoakBypassAlertConfirmedDetails;
}

/** Non-zero applied load on a **0 lbs** plan hold (cyclical unload / baseline). */
export interface PullTestZeroTargetNonzeroAppliedLoadDetails extends PullTestHoldStepActionDetailsBase {
  appliedLoadEnteredLbs: number;
  /** Set when the operator chose **Move on** instead of entering 0 lbs. */
  operatorDecision?: 'move_on';
}

export interface PullTestZeroTargetNonzeroAppliedLoadActionPayload {
  moduleType: PullTestingProjectActionModuleType;
  actionType: 'pull_test_zero_target_nonzero_applied_load';
  pullTestPilingID: string;
  context: PullTestProjectActionContext;
  details: PullTestZeroTargetNonzeroAppliedLoadDetails;
}

/** Install saved when drive times and/or actual embedment exceed assigned (design) depth. */
export interface PullTestDriveTimePastAssignedEmbedment {
  /** Foot index cap derived from {@link assignedEmbedmentDepthFt}. */
  assignedEmbedmentFootCap: number;
  /** Per-foot segments recorded above the assigned cap. */
  pastEmbedmentFeet: Array<{ footNumber: number; driveTimeSec: number }>;
}

export interface PullTestInstallationPastEmbedmentDepthDetails {
  /** Operator-facing pile tag (e.g. `PT-12A`). */
  displayId?: string;
  /** Assigned embedment depth from the piling record (design target). */
  assignedEmbedmentDepthFt: number;
  /** Operator-recorded actual embedment on this install, when captured. */
  actualEmbedmentDepthFt?: number | null;
  /** Present when drive-time segments were recorded past assigned depth. */
  driveTimePastAssignedEmbedment?: PullTestDriveTimePastAssignedEmbedment | null;
  /** Which install signals triggered this audit row. */
  triggers: {
    driveTimePastAssigned: boolean;
    actualPastAssigned: boolean;
  };
}

export interface PullTestInstallationPastEmbedmentDepthActionPayload {
  moduleType: PullTestingProjectActionModuleType;
  actionType: 'pull_test_installation_past_embedment_depth';
  pullTestPilingID: string;
  context: PullTestProjectActionContext;
  details: PullTestInstallationPastEmbedmentDepthDetails;
}

/** Union of client POST bodies for `POST …/pull-testing/project-actions`. */
export type PullTestingProjectActionPayload =
  | PullTestDeflectionGaugeMisalignmentActionPayload
  | PullTestAppliedLoadExceedsNextTargetActionPayload
  | PullTestMaxDeflectionExceededActionPayload
  | PullTestEmptyLoadStepSkippedActionPayload
  | PullTestMonotonicDeflectionOverrideActionPayload
  | PullTestValidationAlertDismissedActionPayload
  | PullTestSoakBypassAlertConfirmedActionPayload
  | PullTestZeroTargetNonzeroAppliedLoadActionPayload
  | PullTestInstallationPastEmbedmentDepthActionPayload;

/** Payload minus `moduleType` and `context` (filled at POST time). */
export type PullTestingProjectActionLogScratch = Omit<
  PullTestingProjectActionPayload,
  'context' | 'moduleType'
>;

/** Assembles the POST body for `POST …/pull-testing/project-actions`. */
export function buildPullTestingProjectActionPayload(
  scratch: PullTestingProjectActionLogScratch,
  context: PullTestProjectActionContext
): PullTestingProjectActionPayload {
  return {
    moduleType: PULL_TESTING_PROJECT_ACTION_MODULE_TYPE,
    ...scratch,
    context,
  } as PullTestingProjectActionPayload;
}

/**
 * High-level project (API: `GET/POST/PUT /projects`).
 * Piled and Pull Testing are separate module endpoints; some responses may still include optional `piled` / `pullTesting`.
 * Client code may omit embeds with {@link stripProjectModuleEmbeds} when storing the high-level row next to sibling module objects.
 */
export interface Project {
  projectID: string;
  name: string;
  status: ProjectStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  location: Location;
  startDate: string;
  /** Legacy top-level field still used by some portal views. */
  pilingType?: PilingType;
  /** External integration ID; set on high-level project create/update, not on Piled module create. */
  externalProjectID?: string;
  endDate?: string;
  /** When true, report generation is disabled for the project (GlobalAdmin may set via PUT /projects). */
  isDelinquent?: boolean | string;
  companies?: ProjectCompany[];
  newActivity?: boolean;
  /** From `GET /projects/{id}/piled` (or after create/update piled config). */
  piled?: PiledModule | null;
  /** From `GET /projects/{id}/pull-testing` when merged into the project object for UI. */
  pullTesting?: PullTestingModule | null;
}

/** Returns a copy of `project` without `piled` / `pullTesting` (high-level row + façade sibling model). */
export function stripProjectModuleEmbeds(project: Project): Project {
  const { piled: _p, pullTesting: _pt, ...rest } = project as Project & {
    piled?: unknown;
    pullTesting?: unknown;
  };
  return rest as Project;
}

export interface PilingAttribute {
  name: string;
  type: 'number' | 'string' | 'boolean' | 'singleSelect' | 'multiSelect';
  metric: string;
  data?: string[];
}

export enum PilingAttributeName {
  WaterPresent = 'Water Present',
  DepthToWater = 'Depth to Water',
  HoleDepth = 'Hole Depth',
  TorqueSpec1 = 'Torque Spec 1',
  TorqueSpec2 = 'Torque Spec 2',
  TorqueSpec3 = 'Torque Spec 3',
  TorqueSpec4 = 'Torque Spec 4',
  StickOutHeight = 'Stick Out Height',
  NorthSouth = 'North South',
  EastWest = 'East West',
  TimeToDrive = 'Time to Drive',
  TimePerFoot = 'Time per foot',
  NumberOfExtensions10ft = 'Number of Extensions 10ft',
  NumberOfExtensions5ft = 'Number of Extensions 5ft',
  TotalFeetInstalled = 'Total Feet Installed',
  Twist = 'Twist',
}

export interface ProjectStatusModel {
  totalPilings: number;
  totalRefused: number;
  awaitingInstallation: {
    count: number;
    percentage: number;
  };
  awaitingQC: {
    count: number;
    percentage: number;
  };
  awaitingRework: {
    count: number;
    percentage: number;
  };
  installed: {
    count: number;
    percentage: number;
  };
  escalated: {
    count: number;
    percentage: number;
  };
}

export const pilingAttributes = {
  Screw: [
    { name: PilingAttributeName.WaterPresent, type: 'boolean', metric: '' },
    { name: PilingAttributeName.DepthToWater, type: 'number', metric: 'in' },
    { name: PilingAttributeName.HoleDepth, type: 'number', metric: 'in' },
    { name: PilingAttributeName.TorqueSpec1, type: 'number', metric: 'ft-lb' },
    { name: PilingAttributeName.TorqueSpec2, type: 'number', metric: 'ft-lb' },
    { name: PilingAttributeName.TorqueSpec3, type: 'number', metric: 'ft-lb' },
    { name: PilingAttributeName.TorqueSpec4, type: 'number', metric: 'ft-lb' },
    { name: PilingAttributeName.StickOutHeight, type: 'number', metric: 'in' },
    { name: PilingAttributeName.NorthSouth, type: 'number', metric: 'degrees' },
    { name: PilingAttributeName.EastWest, type: 'number', metric: 'degrees' },
  ],
  Pounding: [
    { name: PilingAttributeName.TimeToDrive, type: 'number', metric: 'sec' },
    { name: PilingAttributeName.StickOutHeight, type: 'number', metric: 'in' },
    { name: PilingAttributeName.Twist, type: 'number', metric: 'degrees' },
    { name: PilingAttributeName.NorthSouth, type: 'number', metric: 'degrees' },
    { name: PilingAttributeName.EastWest, type: 'number', metric: 'degrees' },
  ],
  Helical: [
    { name: PilingAttributeName.TotalFeetInstalled, type: 'number', metric: 'ft' },
    { name: PilingAttributeName.NumberOfExtensions10ft, type: 'number', metric: '' },
    { name: PilingAttributeName.NumberOfExtensions5ft, type: 'number', metric: '' },
  ],
};

/** Body for `POST /projects` (high-level only). */
export interface CreateProjectPayload {
  name: string;
  companyIDs: string[];
  location?: Location;
  /** When omitted, the API may default (e.g. to creation time). */
  startDate?: string;
  createdBy: string;
  status?: ProjectStatus;
  endDate?: string;
  /** External integration identifier (project-level only). */
  externalProjectID?: string;
}

/** Body for `POST /projects/{projectID}/piled`. */
export interface CreatePiledModuleRequest {
  numPilings: number;
  pilingType: PilingType;
  pilingAttributes: PilingAttribute[];
  reworkAttributes?: PilingAttribute[];
  qcQuickHits?: string[];
  reworkQuickHits?: string[];
  /** Completed-rework quick hits (same as reworkCompleteQuickHits on module) */
  reworkCompleteQuickHits?: string[];
  operatorDisplayFields?: string[];
  operatorDisplayFieldTypes?: OperatorDisplayFieldTypes;
  coordinateSystemType?: 'UTM' | 'StatePlane';
  coordinateSystemZone?: number | string;
  coordinateSystemHemisphere?: 'N' | 'S';
  coordinateSystemUnits?: 'm' | 'us-ft' | 'ft';
  mapEnabled?: boolean;
}

export type UpdatePiledModuleRequest = Partial<Omit<CreatePiledModuleRequest, 'pilingType'>> & {
  pilingType?: PilingType;
  invoicedStatus?: ModuleInvoicedStatus;
  invoicedDate?: string | null;
  /**
   * Optional lifecycle `status` on **`PUT`** only: **`active`** or **`completed`** (not `pending`).
   * See API: `PUT /projects/{projectID}/piled`.
   */
  status?: 'active' | 'completed';
};

/** @deprecated Use CreateProjectPayload + CreatePiledModuleRequest and two API calls. */
export interface CreateProjectRequest {
  name: string;
  companyIDs: string[];
  pilingType: PilingType;
  pilingAttributes: PilingAttribute[];
  location?: Location;
  startDate: string;
  createdBy: string;
  status?: ProjectStatus;
  endDate?: string;
  numPilings?: number;
  reworkAttributes?: PilingAttribute[];
  qcQuickHits?: string[];
  coordinateSystemType?: 'UTM' | 'StatePlane';
  coordinateSystemZone?: number | string;
  coordinateSystemHemisphere?: 'N' | 'S';
  coordinateSystemUnits?: 'm' | 'us-ft' | 'ft';
  mapEnabled?: boolean;
}

export interface UpdateProjectRequest {
  companyID: string;
  projectID: string;
  name?: string;
  description?: string;
  status?: ProjectStatus;
  location?: Location;
  client?: string;
  startDate?: string;
  endDate?: string;
  budget?: number;
  notes?: string;
  /** GlobalAdmin only — when true, report generation is disabled for the project. */
  isDelinquent?: boolean | string;
}

export interface CompanyProjectAssignment {
  companyID: string;
  projectID: string;
  createdAt: string;
  createdBy: string;
}

export interface UserProjectAssignment {
  userID: string;
  projectID: string;
  role?: string;
  createdAt: string;
  createdBy: string;
  lastViewedTimestamp?: string;
  mapDisclaimerAcknowledgedTimestamp?: string;
}

export interface ProjectRequest {
  firstName: string;
  lastName: string;
  company: string;
  companyID: string;
  phoneNumber: string;
  email: string;
  projectName: string;
  projectAddress: string;
  totalPilings: number;
  /** Omitted on new requests when not collected; legacy requests may still include this. */
  numberOfArrays?: number;
  anticipatedStartDate: string;
  pilingType: string;
  pilingAttributes: PilingAttribute[];
  reworkAttributes: PilingAttribute[];
  quickHits: string[];
  qcQuickHits: string[];
  reworkQuickHits: string[];
  completedReworkQuickHits: string[];
  projectCSV?: string;
  csvFileName: string;
  selectedUsers?: string[];
  additionalUsers?: {
    fullName: string;
    email: string;
    role: string;
  }[];
  selectedExternalUsers?: string[];
}

export interface ProjectRequestResponse {
  data: {
    message: string;
    requestId: string;
    submittedAt: string;
    csvS3Key: string;
  };
  message: string;
  code: string;
}

export interface ProjectRequestSummary {
  requestId: string;
  projectName: string;
  company: string;
  firstName: string;
  lastName: string;
  email: string;
  submittedAt: string;
  hasCSV: boolean;
  csvFileName: string;
}

export interface ProjectRequestListResponse {
  requests: ProjectRequestSummary[];
  pagination: {
    hasMore: boolean;
    nextToken: string;
  };
}

export interface ProjectRequestDetail {
  requestId: string;
  submittedAt: string;
  firstName: string;
  lastName: string;
  company: string;
  phoneNumber: string;
  email: string;
  projectName: string;
  projectAddress: string;
  totalPilings: number;
  numberOfArrays?: number;
  anticipatedStartDate: string;
  pilingType: string;
  pilingAttributes: PilingAttribute[];
  reworkAttributes: PilingAttribute[];
  quickHits: string[];
  qcQuickHits: string[];
  reworkQuickHits: string[];
  completedReworkQuickHits: string[];
  csvFileName: string;
  csvS3Key: string;
  csvDownloadUrl: string;
  existingUsers?: {
    name: string;
    companyID: string;
    role: string;
    phoneNumber: string;
    userID: string;
    email: string;
  }[];
  newUsers?: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    role: string;
    email: string;
  }[];
}

/** Company grouping for add-user directory (built client-side from `GET /users?companyID=…` per project company). */
export interface ProjectDirectoryCompanyGroup {
  companyID: string;
  companyName: string;
  isOwnOrg: boolean;
  users: User[];
}

/** Users visible for adding to a project, grouped by company. */
export interface ProjectDirectory {
  companies: ProjectDirectoryCompanyGroup[];
}
